<script>
  import { page, router } from '@inertiajs/svelte';
  import { onMount } from 'svelte';
  import Lightbox from '$lib/lightbox/Index.svelte';
  import responsive from '@/js/utils/responsive';
  import { createLoadObserver } from '@/js/utils/loadObserver';
  import { fade } from 'svelte/transition';

  export let hero;

  $: innerHeight = 0;
  let innerWidth = 0;

  let smallScreen = false;
  let xsScreen = false;
  $: mobile = innerWidth <= responsive.s;

  onMount(() => {
    handleScreenSize();
  });

  router.on('navigate', () => {
    if (window.location.search.includes('showreel')) {
      openLightbox();
    } else {
      closeLightbox();
    }
  });

  function handleScreenSize() {
    innerHeight < responsive.extraSmallHeight
      ? (xsScreen = true)
      : innerHeight < responsive.smallHeight
        ? (smallScreen = true)
        : '';
  }

  //Handle loading
  // let loading = true;

  const onload = createLoadObserver(() => {
    loading = !loading;
  });

  // Lightbox
  let openLightbox, closeLightbox;

  let lightbox = {
    type: 'video',
    items: hero.video,
  };

  // Handle play click
  function handlePlayClick() {
    // Append showreel=1 to the URL
    const url = new URL(window.location.href);
    url.searchParams.set('showreel', '1');
    router.visit(url);
  }
</script>

<svelte:window bind:innerHeight bind:innerWidth on:resize={handleScreenSize} />

<section
  class="page-hero"
  class:page-hero--simple={!hero.image}
  class:page-hero--static={hero.static}
>
  <div class="page-hero__bg">
    {#if hero.image && !mobile}
      <div class="page-hero__bg-content">
        <img
          class="page-hero__bg-img"
          src={hero.image.src}
          width={hero.image.width}
          height={hero.image.height}
          alt={hero.image.alt}
          preload
        />
        <div class="page-hero__bg-layer page-hero__bg-noise" />
      </div>
      <div class="page-hero__bg-layer page-hero__bg-visual" />
    {:else}
      <div class="page-hero__bg-layer page-hero__bg-el" />
    {/if}
    <div class="page-hero__bg-layer page-hero__bg-gradient" />
  </div>
  <div class="container container--large">
    <div class="page-hero__content">
      <div class="page-hero-content" class:xsScreen class:smallScreen>
        {#if hero.image}
          <img
            height="80"
            width="42"
            class="page-hero-content__icon"
            src={`${$page.props.asset_url}img/logo-element.svg`}
            alt="//"
          />
        {/if}
        <h1 class="page-hero-content__title">
          {@html hero.title}
        </h1>
        {#if hero.desc}
          <p class="page-hero-content__desc">{@html hero.desc}</p>
        {/if}
      </div>
      {#if !mobile}
        <div class="page-hero__line" />
      {/if}
      {#if hero.image}
        <div class="page-hero__image-small">
          <img
            class="page-hero__bg-img"
            src={hero.image.src}
            width={hero.image.width}
            height={hero.image.height}
            alt={hero.image.alt}
            preload
          />
        </div>
      {/if}
    </div>
    {#if hero.image}
      <div class="page-hero__bottom">
        <div class="container container--large">
          <div class="flexbox v-e">
            <div class="col col-s-full page-hero__bottom-btn-wrap">
              {#if hero.video}
                <button
                  class="btn btn-icon btn-main"
                  class:btn-main--light={!mobile}
                  class:btn-main--dark={mobile}
                  on:click={handlePlayClick}
                  data-video={hero.video}
                >
                  <span>{$page.props.i18n.play_showreel}</span>
                  <i class="isax isax-play" />
                </button>
              {/if}
            </div>
            <div class="col m-left" />
          </div>
        </div>
      </div>
    {/if}
  </div>
</section>
{#if hero.video}
  <Lightbox {lightbox} bind:open={openLightbox} bind:close={closeLightbox} />
{/if}

<style lang="scss">
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .page-hero {
    @include resp-min('s') {
      z-index: 0;
    }

    position: relative;

    height: 93vh;
    color: rgb(var(--white));

    padding: 0;

    @include resp-max('s') {
      color: var(--royal);
      height: auto;
      border-bottom: 1px solid $light-border;
    }

    &--simple {
      color: inherit;
      height: auto;

      margin: 0 0 10rem;

      @include resp-max('s') {
        margin: 0 0 5rem;
      }
    }

    &__bg {
      z-index: -1;

      &-content {
        height: 100%;
      }

      &,
      &-layer {
        position: absolute;
        inset: 0;
      }

      &-img {
        position: relative;
        z-index: -1;

        width: 100%;
        height: 100%;

        object-fit: cover;
      }

      &-noise {
        z-index: 0;

        opacity: 0.05;
        background: url(img/noise.png) 300px 300px;
      }

      &-gradient {
        z-index: 2;

        opacity: 0.1;

        .page-hero--simple & {
          opacity: 0.05;
        }

        @include gradient('blue-white', 'to bottom');
      }

      &-visual {
        z-index: 1;

        opacity: 0.8;

        @include gradient('grey', 'to top');
      }

      &-el {
        margin: 10rem 0 0;

        background: {
          // image: url(img/brand-bg-el-dark.svg);
          image: url(https://www.caballero.lv/themes/base/dist/img/brand-bg-el-dark.svg);
          size: auto 32rem;

          position: center top;
          repeat: no-repeat;
        }

        @include resp-max('xs') {
          background-size: auto 24rem;
        }
      }
    }

    &__content {
      padding: 10rem 0 0;
      height: 100%;
      text-align: center;

      @include resp-max('s') {
        padding: 14rem 0 0;
      }
    }

    &__line {
      .page-hero--simple & {
        height: 1px;
        background-color: $light-border;
      }
    }

    &__image-small {
      @include resp-min('s') {
        display: none;
      }

      aspect-ratio: 330/216;
      position: relative;
      border-radius: 2rem 0;
      overflow: hidden;
      margin: 6rem 0 0;

      img {
        object-fit: cover;
        height: 100%;
        width: 100%;
      }
    }

    &__bottom {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 7rem;

      @include resp-max('s') {
        position: static;
        margin-bottom: 5rem;
        &-btn-wrap {
          display: flex;
          justify-content: center;

          .btn {
            margin-top: 5rem;
          }
        }
      }
    }
  }

  .page-hero-content {
    max-width: 80rem;
    margin: 0 auto;

    .page-hero--simple & {
      padding: 9.2rem 0 9.3rem;

      @include resp-max('s') {
        padding: 0 0 5rem;
      }
    }

    &__icon {
      height: 8rem;
      margin: 17rem auto 2rem;

      @include resp-max('s') {
        display: none;
      }
    }

    &__desc {
      margin: 2rem 0 0;
    }

    &__title {
      @include resp-max('s') {
        font-size: 5rem;
      }
      @media (min-device-pixel-ratio: 1.5) and (min-width: 900px),
        (-webkit-min-device-pixel-ratio: 1.5) and (min-width: 900px) {
        font-size: 5.3rem;
      }
    }
  }

  .page-hero-content.xsScreen {
    .page-hero-content__icon {
      margin-top: 7rem;
    }
    .page-hero-content__title {
      font-size: 6rem;

      @include resp-max('s') {
        font-size: 5.3rem;
      }

      @include resp-max('xs') {
        font-size: 4rem;
      }

      @media (min-device-pixel-ratio: 1.5) and (min-width: 900px),
        (-webkit-min-device-pixel-ratio: 1.5) and (min-width: 900px) {
        font-size: 5.3rem;
      }
    }
  }

  .hero-loader {
    position: absolute;
    z-index: 10;
    inset: 0;

    background: rgb(var(--white));

    display: flex;
    align-items: center;
    justify-content: center;

    &__spinner {
      position: absolute;
      top: 50%;
      left: 50%;
      margin: {
        top: -15rem;
        left: -15rem;
      }

      display: block;
      width: 30rem;
      height: 30rem;
      border-radius: 50%;

      mask-size: contain;

      background: conic-gradient(
        from 0.25turn at 50%,
        var(--aqua-2),
        rgb(var(--white))
      );

      animation: spin 3s infinite linear;
    }

    &__logo {
      width: 10rem;
    }
  }

  :global .page-single-vacancy {
    .page-hero {
      @include resp-max('s') {
        margin-bottom: 5rem;
      }
    }
  }
</style>
